import React, { Fragment, useState } from "react";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { Btn, H4, P } from "../AbstractElements";
import { EmailAddress, Password, SignIn } from "../Constant";
import { Link, useNavigate } from "react-router-dom";
import { useContext } from "react";
import CustomizerContext from "../_helper/Customizer";
import credoraToast from "../Components/Credora/Common/credoraToast";
import { Eye, EyeOff, Lock, Mail, Shield } from "react-feather";
import { getStoredUser, login, resendEmail2faCode, verifyEmail2faLogin } from "../api/auth";
import { roleHomePath } from "../constants/menuAccess";
import { DEV_LOGIN_USERS, isLocalDevLoginEnabled } from "./devLoginUsers";
import EmailOtpInput from "./EmailOtpInput";

import { brandLogo } from "../assets/brand/credoraLogos";
import authIllustration from "../assets/images/login/auth-img.svg";

const FEATURES = [
  "Executive dashboards & portfolio analytics",
  "Borrower and loan lifecycle management",
  "Compliance-ready audit trails",
];

const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [email2faCode, setEmail2faCode] = useState("");
  const [email2faChallenge, setEmail2faChallenge] = useState(null);
  const [showMfa, setShowMfa] = useState(false);
  const [togglePassword, setTogglePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const history = useNavigate();
  const { layoutURL } = useContext(CustomizerContext);

  const onEmail2faStep = Boolean(email2faChallenge);
  const canSubmitCredentials =
    email.trim().length > 0 && password.length > 0 && !submitting && !onEmail2faStep;
  const canSubmitEmail2fa =
    onEmail2faStep && email2faCode.length === 6 && !submitting && (!showMfa || mfaCode.length === 6);
  const showDevQuickLogin = isLocalDevLoginEnabled() && !onEmail2faStep;

  const redirectAfterLogin = () => {
    history(roleHomePath(getStoredUser(), layoutURL));
    credoraToast.success("Signed in successfully.");
  };

  const performLogin = async (loginEmail, loginPassword, loginMfa = "") => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(loginEmail.trim(), loginPassword, loginMfa || undefined);
      if (result?.email2faRequired) {
        setEmail2faChallenge({
          challengeId: result.challengeId,
          emailHint: result.emailHint,
          expiresInMinutes: result.expiresInMinutes,
        });
        setEmail2faCode("");
        setMfaCode("");
        setShowMfa(false);
        credoraToast.success(result.message || "Check your email for a verification code.");
        return;
      }
      redirectAfterLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      credoraToast.error(detail, "Incorrect email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyEmail2fa = async (e) => {
    e.preventDefault();
    if (!canSubmitEmail2fa || !email2faChallenge) {
      return;
    }
    setSubmitting(true);
    try {
      await verifyEmail2faLogin({
        challengeId: email2faChallenge.challengeId,
        code: email2faCode,
        mfaCode: showMfa ? mfaCode : undefined,
      });
      setEmail2faChallenge(null);
      redirectAfterLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "MFA code required") {
        setShowMfa(true);
        credoraToast.error("Enter your authenticator MFA code to continue.");
      } else {
        credoraToast.error(detail, "Could not verify the email code.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!email2faChallenge || resendingCode) {
      return;
    }
    setResendingCode(true);
    try {
      const result = await resendEmail2faCode(email2faChallenge.challengeId);
      setEmail2faChallenge({
        challengeId: result.challengeId,
        emailHint: result.emailHint,
        expiresInMinutes: result.expiresInMinutes,
      });
      setEmail2faCode("");
      credoraToast.success(result.message || "A new code has been sent.");
    } catch (err) {
      credoraToast.error(err.response?.data?.detail, "Could not resend the code.");
    } finally {
      setResendingCode(false);
    }
  };

  const backToCredentials = () => {
    setEmail2faChallenge(null);
    setEmail2faCode("");
    setMfaCode("");
    setShowMfa(false);
  };

  const loginAuth = async (e) => {
    e.preventDefault();
    if (!canSubmitCredentials) {
      return;
    }
    await performLogin(email, password, mfaCode);
  };

  const quickLoginAs = async (user) => {
    setEmail(user.email);
    setPassword(user.password);
    setMfaCode("");
    setShowMfa(false);
    setEmail2faChallenge(null);
    setEmail2faCode("");
    await performLogin(user.email, user.password);
  };

  return (
    <Fragment>
      <div className="credora-login">
        <div className="credora-login__shell">
          <aside className="credora-login__brand" aria-hidden="true">
            <div className="credora-login__brand-inner">
              <p className="credora-login__eyebrow">Loan management platform</p>
              <h1 className="credora-login__headline">
                Credit operations,
                <span> one secure workspace</span>
              </h1>
              <P attrPara={{ className: "credora-login__tagline" }}>
                Manage borrowers, loans, and compliance from a single system built for lending teams.
              </P>
              <ul className="credora-login__features">
                {FEATURES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="credora-login__illustration">
                <img src={authIllustration} alt="" />
              </div>
            </div>
          </aside>

          <main className="credora-login__panel">
            <div className="credora-login__card">
              <Link className="credora-login__logo credora-brand-logo" to="/">
                <img src={brandLogo} alt="Credo" className="img-fluid credora-brand-logo__img" />
              </Link>

              <div className="credora-login__form-header">
                <H4>{onEmail2faStep ? "Verify your email" : "Sign in to Credora360"}</H4>
                <P>
                  {onEmail2faStep
                    ? `Enter the 6-digit code sent to ${email2faChallenge.emailHint}. It expires in ${email2faChallenge.expiresInMinutes} minutes.`
                    : "Use your staff credentials to access the platform."}
                </P>
              </div>

              {onEmail2faStep ? (
                <Form className="theme-form credora-login__form" onSubmit={verifyEmail2fa} noValidate>
                  <FormGroup>
                    <Label className="col-form-label text-center d-block" for="credora-email-2fa-0">
                      Verification code
                    </Label>
                    <EmailOtpInput
                      idPrefix="credora-email-2fa"
                      value={email2faCode}
                      onChange={setEmail2faCode}
                      autoFocus
                      disabled={submitting}
                    />
                  </FormGroup>

                  {showMfa ? (
                    <FormGroup className="credora-login__mfa">
                      <Label className="col-form-label" for="credora-mfa">
                        Authenticator MFA code
                      </Label>
                      <div className="credora-login__input-wrap">
                        <Shield className="credora-login__input-icon" size={18} aria-hidden="true" />
                        <Input
                          id="credora-mfa"
                          className="form-control credora-login__input"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="6-digit code"
                          autoComplete="one-time-code"
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                          value={mfaCode}
                        />
                      </div>
                    </FormGroup>
                  ) : null}

                  <Btn
                    attrBtn={{
                      color: "primary",
                      className: "credora-login__submit d-block w-100",
                      type: "submit",
                      disabled: !canSubmitEmail2fa,
                    }}>
                    {submitting ? (
                      <>
                        <span className="credora-login__spinner" aria-hidden="true" />
                        Verifying…
                      </>
                    ) : (
                      "Continue"
                    )}
                  </Btn>

                  <p className="text-center mt-3 mb-0" style={{ fontSize: "14px" }}>
                    <button
                      type="button"
                      className="credora-login__mfa-toggle btn btn-link d-inline"
                      disabled={resendingCode}
                      onClick={handleResendCode}>
                      {resendingCode ? "Sending…" : "Resend code"}
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="credora-login__mfa-toggle btn btn-link d-inline"
                      onClick={backToCredentials}>
                      Back to sign in
                    </button>
                  </p>
                </Form>
              ) : (
                <Form className="theme-form credora-login__form" onSubmit={loginAuth} noValidate>
                  <FormGroup>
                    <Label className="col-form-label" for="credora-email">
                      {EmailAddress}
                    </Label>
                    <div className="credora-login__input-wrap">
                      <Mail className="credora-login__input-icon" size={18} aria-hidden="true" />
                      <Input
                        id="credora-email"
                        className="form-control credora-login__input"
                        type="email"
                        required
                        autoComplete="username"
                        autoFocus
                        placeholder="you@company.com"
                        onChange={(e) => setEmail(e.target.value)}
                        value={email}
                      />
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label className="col-form-label" for="credora-password">
                      {Password}
                    </Label>
                    <div className="credora-login__input-wrap">
                      <Lock className="credora-login__input-icon" size={18} aria-hidden="true" />
                      <Input
                        id="credora-password"
                        className="form-control credora-login__input credora-login__input--password"
                        type={togglePassword ? "text" : "password"}
                        required
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        onChange={(e) => setPassword(e.target.value)}
                        value={password}
                      />
                      <button
                        type="button"
                        className="credora-login__toggle-password"
                        onClick={() => setTogglePassword(!togglePassword)}
                        aria-label={togglePassword ? "Hide password" : "Show password"}>
                        {togglePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </FormGroup>

                  {showMfa ? (
                    <FormGroup className="credora-login__mfa">
                      <Label className="col-form-label" for="credora-mfa-login">
                        MFA code
                      </Label>
                      <div className="credora-login__input-wrap">
                        <Shield className="credora-login__input-icon" size={18} aria-hidden="true" />
                        <Input
                          id="credora-mfa-login"
                          className="form-control credora-login__input"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="6-digit code"
                          autoComplete="one-time-code"
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                          value={mfaCode}
                        />
                      </div>
                    </FormGroup>
                  ) : (
                    <button
                      type="button"
                      className="credora-login__mfa-toggle btn btn-link"
                      onClick={() => setShowMfa(true)}>
                      Use MFA code
                    </button>
                  )}

                  <Btn
                    attrBtn={{
                      color: "primary",
                      className: "credora-login__submit d-block w-100",
                      type: "submit",
                      disabled: !canSubmitCredentials,
                    }}>
                    {submitting ? (
                      <>
                        <span className="credora-login__spinner" aria-hidden="true" />
                        Signing in…
                      </>
                    ) : (
                      SignIn
                    )}
                  </Btn>
                </Form>
              )}

              {showDevQuickLogin && (
                <div className="credora-login__dev-hint" role="note">
                  <strong>Local dev — quick sign-in</strong>
                  <p className="credora-login__dev-hint-text">
                    One-click login with seeded demo users. Not shown in production builds.
                  </p>
                  <div className="credora-login__dev-buttons">
                    {DEV_LOGIN_USERS.map((user) => (
                      <button
                        key={user.email}
                        type="button"
                        className="credora-login__dev-btn"
                        disabled={submitting}
                        onClick={() => quickLoginAs(user)}>
                        {submitting && email === user.email ? "Signing in…" : user.role}
                      </button>
                    ))}
                  </div>
                  <span className="credora-login__dev-hint-cmd">
                    Seed: <code>python -m scripts.seed_admin</code> then <code>python -m scripts.seed_demo</code>
                  </span>
                </div>
              )}

              <p className="credora-login__footer-note">
                Protected access for authorized staff only. Activity may be logged for compliance.
              </p>
            </div>
          </main>
        </div>
      </div>
    </Fragment>
  );
};

export default Signin;
